<template>
  <q-card-section class="row justify-between">
    <h6 class="text-primary q-row-gutter-sm">Estimates of Thermal Metrics [Work in Progress]</h6>
    <div class="q-gutter-md">
      <q-btn
        color="primary"
        icon="download"
        flat
        dense
        @click="saveChartAsImage"
      >
        <q-tooltip>
          Save Chart as Image
        </q-tooltip>
      </q-btn>
      <reference-popup-proxy
        icon="warning"
        color="red"
        tooltip="Under active development"
        size="md"
        text="This is a WORST CASE which uses conservative derating (i.e., low attenuation due to the skull but high absorption in the brain)
         and does not account for heat dissipation due to conduction. Heat dissipation due to blood perfusion is accounted for.
          The reported temperatures are in the brain and not in the skull.
         Temperature in the skull is likely to be higher. For bone safety, consider using TIC or, for maximum certainty, run individualized simulations."
      />
    </div>
  </q-card-section>
  <q-card-section class="row flex justify-center q-gutter-md">
    <q-input
      class="col-4"
      v-if="!isManualExposureTime"
      :dark="isDarkModeGlobal"
      :label="`Graph Duration [${tempRiseTimeUnitLabel}]`"
      outlined
      type="number"
      step="0.000001"
      readonly
      :model-value="calculatorStore.overallExposureTime"
      :rules="exposureTimeRules"
      hide-bottom-space
    >
      <template #append>
        <q-icon
          name="edit"
          @click="switchToManualExposureTime(true)"
        >
          <q-tooltip>
            Set your own value for exposure time
          </q-tooltip>
        </q-icon>
      </template>
    </q-input>

    <q-input
      v-else
      class="col-4"
      :dark="isDarkModeGlobal"
      :label="`Graph Duration [${tempRiseTimeUnitLabel}]`"
      filled
      type="number"
      step="0.000001"
      :rules="exposureTimeRules"
      v-model="overallExposureTimeManual"

      hide-bottom-space
    >
      <template #append>
        <q-icon
          name="refresh"
          @click="switchToManualExposureTime(false)"
        >
          <q-tooltip>
            Use exposure time calculated value
          </q-tooltip>
        </q-icon>
      </template>
    </q-input>
    <q-select
      class="col-4"
      :dark="isDarkModeGlobal"
      :options="timeOptions"
      label="X-axis Time Unit"
      filled
      type="text"
      v-model="tempRiseTimeUnit"
      hint=""
      :hide-bottom-space="true"
    />
  </q-card-section>
  <q-card-section class="row flex justify-center q-gutter-md">
    <v-chart
      ref="tempRiseChart"
      id="tempRiseChart"
      class="chart"
      style="width: 800px; height: 260px;"
      @change="generateValues"
      :init-options="initOptions"
      autoresize
      :manual-update="true"
    />
    <v-chart
      ref="thermalDoseChart"
      id="thermalDoseChart"
      class="chart"
      style="width: 800px; height: 260px;"
      :init-options="initOptions"
      autoresize
      :manual-update="true"
    />
  </q-card-section>
</template>
<script lang="ts" setup>
import * as echarts from 'echarts/core'
import { SVGRenderer } from 'echarts/renderers'
import { useCalculatorStore } from '../stores/calculator.store'
import {
  TitleComponent,
  ToolboxComponent,
  TooltipComponent,
  GridComponent,
  LegendComponent,
  GraphicComponent
} from 'echarts/components'
import { LineChart } from 'echarts/charts'
import { UniversalTransition } from 'echarts/features'
import VChart from 'vue-echarts'
import {computed, onMounted, ref, watch} from 'vue';
import type ECharts from "vue-echarts";
import {storeToRefs} from "pinia";
import ReferencePopupProxy from "./PopupProxyLiteratureReference.vue";
import {EChartsInitOpts} from "echarts/core";
const calculatorStore = useCalculatorStore()

echarts.use([
  TitleComponent,
  ToolboxComponent,
  TooltipComponent,
  GridComponent,
  LegendComponent,
  LineChart,
  SVGRenderer,
  UniversalTransition,
  GraphicComponent
])
const {
  isDarkModeGlobal,
  MR_SAR,
  density_of_white_matter_kg_m3,
  overallExposureTime,
  overallExposureTimeManual,
  isManualExposureTime,
  max_temp_rise_us_protocol,
  max_temp_rise_steady_state,
  max_thermal_dose,
  importingData,
  tempRiseTimeUnit
} = storeToRefs(calculatorStore)

const thermalDoseChart = ref<InstanceType<typeof ECharts> | null>(null)
const tempRiseChart = ref<InstanceType<typeof ECharts> | null>(null)
const timeOptions = ref(['seconds', 'minutes'])

const tempRiseTimeUnitLabel = computed(() => tempRiseTimeUnit.value === 'minutes' ? 'min' : 's')
const exposureTimeBaseSwitch = computed(() => tempRiseTimeUnit.value === 'minutes' ? calculatorStore.exposureTimeBaseInSec / 60 : calculatorStore.exposureTimeBaseInSec)
const validExposureTime = (val: number) => val >= exposureTimeBaseSwitch.value
const exposureTimeRules = [(val: number) => validExposureTime(val) || 'This cannot be lower than the total ultrasound duration.']

const nSteps = 1000
const ctBlood = 3600
const ctWm = 3600
const rhoBlood = 1057
const wWm = 0.00395

const tempRiseTime =  ref<number[]>([])
const tempRise = ref<number[]>([]);
const thermalDose = ref<number[]>([]);

const initOptions : EChartsInitOpts = {
  renderer: "svg",
  devicePixelRatio: 2,
}

function switchToManualExposureTime(bool: boolean) {
  overallExposureTimeManual.value = calculatorStore.overallExposureTime
  isManualExposureTime.value = bool
}

const exposureTimeInSeconds = computed(() => {
  let time = isManualExposureTime.value ? overallExposureTimeManual.value : overallExposureTime.value
  if (tempRiseTimeUnit.value === 'minutes') {
    return time * 60
  }
  return time
})

const fractionDigits = computed(() => exposureTimeInSeconds.value < 5 ? 4 : 3)
const fractionDigitsForMinutes = computed(() => exposureTimeInSeconds.value / 60 < 1 ? 6 : 4)
const dt = computed(() => exposureTimeInSeconds.value / nSteps)
const tusMaxSarAtFocus = computed(() => calculatorStore.TUS_Max_SAR_at_Focus);
const mrSar = computed(() => +MR_SAR.value);
const rhoWm = computed(() => density_of_white_matter_kg_m3.value);


const generateValues = () => {
  let indexWithMaxVal = 0
  tempRise.value.length = 0
  thermalDose.value.length = 0
  max_temp_rise_us_protocol.value = 0

  tempRiseTime.value = Array.from({ length: nSteps + 1 },(_, i) => +(i * dt.value))
  let timeInSeconds = tempRiseTime.value.map(x => +x.toFixed(fractionDigits.value))
  let timeInminutes = Array.from({ length: nSteps + 1 },(_, i) => +((i * dt.value) / 60).toFixed(fractionDigitsForMinutes.value))

  let timeBaseInSeconds = calculatorStore.exposureTimeBaseInSec

  tempRiseTime.value.forEach((t, index) => {
    const prevTemp = index === 0 ? 0 : tempRise.value[index - 1] ?? 0
    let sarDtC = index === 0 ? 0 : (calculatorStore.SAR_for_Temperature_Calculation / ctBlood) * dt.value
    const prevThermalDose = index === 0 ? 0 : Number(thermalDose.value[index - 1] ?? 0)
    const wDtDt =
      (wWm * prevTemp * dt.value * ctBlood * rhoBlood) / (ctWm * rhoWm.value)

    if (t > timeBaseInSeconds) sarDtC = 0

    const tRise = prevTemp + sarDtC - wDtDt
    tempRise.value.push(tRise)

    if (tRise > max_temp_rise_us_protocol.value) {
      max_temp_rise_us_protocol.value = tRise
      indexWithMaxVal = index
    }

    // thermal dose calculation
    let tDoseFactor = prevTemp > (43 - 37) ? 0.5 : 0.25
    tDoseFactor = Math.pow(tDoseFactor, 43 - (prevTemp + 37))
    const tDose = (tDoseFactor * dt.value) / 60 + prevThermalDose
    thermalDose.value.push(tDose)
  })


  const maxTempRiseChartData = [[indexWithMaxVal, max_temp_rise_us_protocol.value]]
  max_temp_rise_steady_state.value = ((tusMaxSarAtFocus.value + mrSar.value) * rhoWm.value) / (rhoBlood * ctWm * wWm)
  const thermalDoseValue = thermalDose.value ? +(thermalDose.value[thermalDose.value.length - 1]) : 0
  max_thermal_dose.value = thermalDoseValue < 0.001 ? thermalDoseValue : +thermalDoseValue.toFixed(4)

  const commonTextStyle = {
    color: isDarkModeGlobal.value ? "#FFF" : "#333",
    fontFamily: "'Roboto', '-apple-system', 'Helvetica Neue', Helvetica, Arial, sans-serif",
    fontWeight: 'normal'
  };

  const legendTextStyle = {
    ...commonTextStyle,
    fontSize: 11,
  };

  const titleTextStyle = {
    ...commonTextStyle,
    fontSize: 16,
  };

  const grid = {
    left: '15%',
    right: '43%',
    top: '10%',
    bottom: '20%',
  };

  const commonTooltip = () => ({
    trigger: 'axis',
    formatter: function (params) {
      let result = `<div style="text-align:left; padding:5px 10px; border-bottom: 1px solid #eee;">
                  <strong>${params[0].axisValue} ${tempRiseTimeUnitLabel.value}</strong>
                </div>`
      params.forEach(item => {
        result += `<div style="display:flex; justify-content: space-between; padding: 2px 10px;">
                 <span>${item.marker} ${item.seriesName}:</span>
                 <span style="text-align: right; font-weight: bold;">${item.value}</span>
               </div>`
      });
      return result;
    }
  })

  const commonXAxis = () => ({
    name: `Time (${tempRiseTimeUnitLabel.value})`,
    nameLocation: 'middle',
    boundaryGap: true,
    axisLabel: {
      interval: 99,
    },
    minorTick: {
      show: false
    },
    minorSplitLine: {
      show: true
    },
    nameTextStyle: {
      color: commonTextStyle.color,
      padding: 8
    },
    type: 'category',
    data: tempRiseTimeUnit.value === "minutes" ? timeInminutes : timeInSeconds || []
  });

  const commonYAxis = (name: string) => ({
    name: name,
    minorTick: {
      show: true
    },
    minorSplitLine: {
      show: false
    },
    nameLocation: 'center',
    nameTextStyle: {
      color: commonTextStyle.color,
      padding: 50
    },
    type: 'value'
  });

  if (tempRiseChart.value) {
    const seriesTempRise1 = 'Chosen US Protocol in White Matter'
    const seriesTempRise2 =  `${Number(max_temp_rise_us_protocol.value.toPrecision(3))} °C - Max. Temp Rise during Chosen US Protocol`
    const seriesTempRise4 = `${Number(max_temp_rise_steady_state.value.toPrecision(3))} °C - Max. Temp Rise in Steady State in White Matter`

    tempRiseChart.value.setOption({
      darkMode: 'auto',
      color: ['#951818', '#51a148', '#51a148'],
      title: {
        text: 'Temperature Rise Over Time',
        left: '22%',
        top: -5,
        textStyle: titleTextStyle
      },
      tooltip: commonTooltip(),
      grid: grid,
      legend: {
        tooltip: {
          show: true,
          // Optional: customize global legend tooltip style/formatter
          formatter: function (params) {
            if (params.name === seriesTempRise4) {
              return "This value represents the steady state temperature rise that would be achieved in a theoretical scenario<br>" +
                "in which the specified pulse train (or pulse train repeat) is applied indefinitely.<br>" +
                " This steady state is achieved when the heat deposited by the ultrasound equals the heat removed by perfusion.<br>" +
                " We report the value in white matter because the temperature rise in white matter is greater than that in grey matter."
            }
          }
        },
        top: 20,
        type: 'plain',
        left: '58%',
        orient: 'vertical',
        textStyle: legendTextStyle,
        data: [
          seriesTempRise1, seriesTempRise2, seriesTempRise4
        ]
      },
      xAxis: commonXAxis(),
      yAxis: commonYAxis('Temperature Rise (°C)'),
      series: [
        {
          name: seriesTempRise1,
          universalTransition: 'disabled',
          type: 'line',
          lineStyle: { width: 4 },
          data: tempRise.value,
          showSymbol: false,
          animation: false,
        },
        {
          name: seriesTempRise2,
          type: 'scatter',
          data: maxTempRiseChartData,
          symbolSize: 14,
          itemStyle: { color: '#951818'}
        },
        {
          name: seriesTempRise4,
          type: 'line',
          showSymbol: false,
          symbolSize: 12,
          data: []
        },
      ]
    });
  }

  if (thermalDoseChart.value) {
    const series1 = 'Thermal Dose'
    const series2 = `${Number(max_thermal_dose.value.toPrecision(3))} min at 43 °C Max. Thermal Dose`

    thermalDoseChart.value.setOption({
      darkMode: 'auto',
      color: ['#951818', '#51a148'],
      title: {
        text: 'Thermal Dose Over Time',
        left: '23%',
        top: -5,
        textStyle: titleTextStyle
      },
      tooltip: commonTooltip(),
      grid: grid,
      legend: {
        top: 20,
        type: 'plain',
        left: '58%',
        orient: 'vertical',
        textStyle: legendTextStyle,
        data: [series1, series2],
      },
      xAxis: commonXAxis(),
      yAxis: commonYAxis('Thermal Dose (CEM43°C)'),
      series: [
        {
          name: series1,
          type: 'line',
          data: thermalDose.value || [],
          animation: false,
          showSymbol: false,
          lineStyle: { width: 4 },
        },
        {
          name: series2,
          type: 'scatter',
          data: [[nSteps, max_thermal_dose.value]],
          symbolSize: 14,
          itemStyle: { color: '#951818' }
        }
      ]
    });
  }
}

function writeToFile(svgString: string, name: string) {
  const blob = new Blob([svgString], { type: 'image/svg+xml' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url;
  a.download = `${name}.svg`
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function saveChartAsImage () {
  if (tempRiseChart.value) {
    writeToFile(tempRiseChart.value.chart
        .renderToSVGString()
        .replace(/fill="#FFF"/g, 'fill="#333"'), "tempRiseChart");
  }
  if (thermalDoseChart.value) {
    writeToFile(thermalDoseChart.value.chart
        .renderToSVGString()
        .replace(/fill="#FFF"/g, 'fill="#333"'), "thermalDoseChart");
  }
}

watch([
  exposureTimeInSeconds,
  mrSar,
  tusMaxSarAtFocus,
], () => generateValues())

watch(exposureTimeBaseSwitch, (value) => {
  if (!importingData.value && !isManualExposureTime.value) {
    overallExposureTimeManual.value = value * 2
    generateValues()
  }
})

watch(tempRiseTimeUnit, (newUnit, oldUnit) => {
  if (isManualExposureTime.value && newUnit !== oldUnit) {
    overallExposureTimeManual.value = newUnit === 'minutes'
      ? +(overallExposureTimeManual.value / 60).toFixed(4)
      : +(overallExposureTimeManual.value * 60).toFixed(4)
  }
  generateValues()
})

onMounted(() => generateValues())
</script>
